import { Badge, KeyValue as DsKeyValue } from '@orbitalwatch/design';
import { useId, useMemo } from 'react';
import type { ReactNode } from 'react';

export interface PanelProps {
    /** h2 in the head (display 16 px). An empty title with no `count` / `actions` drops the head. */
    title: string;
    /** Count `Badge` next to the title ("Expeditions 4"). */
    count?: number;
    /** Right side of the head (toggles, the small primary). */
    actions?: ReactNode;
    /** Full-width row under the head (filters), `ark-toolbar` layout. */
    toolbar?: ReactNode;
    /** Foot line; two nodes are pushed left / right. */
    footer?: ReactNode;
    /** No body padding (tables, card lists). */
    flush?: boolean;
    className?: string;
    'aria-label'?: string;
    children: ReactNode;
}

/**
 * DS gap: the library has no titled surface with head / toolbar / body / foot (the recipes draw it as
 * `.ow-recipe-panel`); this is that panel on tokens (`ds.css` "panel").
 */
export function Panel(props: PanelProps) {
    const headingId = useId();
    const { title, count, actions } = props;
    const hasHead = !!title || count !== undefined || !!actions;
    const label = props['aria-label'];

    return (
        <section
            className={['ark-panel', props.className].filter(Boolean).join(' ')}
            aria-label={label}
            aria-labelledby={!label && title ? headingId : undefined}
        >
            {hasHead ? (
                <div className="ark-panel-head">
                    <div className="ark-panel-title">
                        {title ? <h2 id={headingId}>{title}</h2> : null}
                        {count !== undefined ? <Badge count={count} tone="neutral" showZero aria-label={`${count} ${title.toLowerCase()}`.trim()} /> : null}
                    </div>
                    {actions ? <div className="ark-panel-actions">{actions}</div> : null}
                </div>
            ) : null}
            {props.toolbar ? <div className="ark-panel-toolbar ark-toolbar">{props.toolbar}</div> : null}
            <div className={props.flush ? 'ark-panel-body is-flush' : 'ark-panel-body'}>{props.children}</div>
            {props.footer ? <div className="ark-panel-foot">{props.footer}</div> : null}
        </section>
    );
}

export interface KeyValueProps {
    items: { label: ReactNode; value: ReactNode; id?: string }[];
    columns?: 1 | 2 | 3;
    dense?: boolean;
}

/** Library `KeyValue` (eyebrow key over a 15 px value); an empty value reads as a muted dash. */
export function KeyValue(props: KeyValueProps) {
    const items = useMemo(() => props.items.map((item, index) => ({
        key: item.label,
        value: item.value ?? <span className="ark-muted">-</span>,
        id: item.id ?? (typeof item.label === 'string' ? item.label : String(index)),
    })), [props.items]);
    return <DsKeyValue items={items} columns={props.columns} dense={props.dense} />;
}

/** The 12 px mono id line under a value (the created / updated ids). */
export function Ident(props: { children: string }) {
    return <span className="ark-ident">{props.children}</span>;
}
